import DOMPurify from 'dompurify';
import { postSegmentService, postTrialInterestBoomiService } from '../../services/trackingServices';
import { gtagEnvelope } from '../gtagEnvelope';
import { FormData, SendAsyncPostObject, SitecoreTrialContentType } from '../../types';
import { getDataResidencyParam } from '../getDataResidencyParam';
import { getCookie } from '../cookies';
import { checkUserValidity, postTrialFormDataServiceLegacy } from '../../services/sitecoreServices';
import { getFlowType } from '../getFlowType';
import { getEventReason } from '../getExperimentVersion';
import { facebookCAPIGTMEvent } from '../facebookCAPIGTMEvent';
import { analyticsEvent } from '../analyticsEvent';

interface FamiliesSubmission {
  data: SitecoreTrialContentType;
  existingUserEmail: string;
  values: SendAsyncPostObject;
  marketingOptOutSeen: boolean;
  contactPermission: boolean;
}

export const handleFamiliesTrialSubmission = async ({
  data,
  existingUserEmail,
  values,
  marketingOptOutSeen,
  contactPermission,
}: FamiliesSubmission) => {
  const { formType, trialLength, segExpQuery, segExpValue } = window.LPFormData;
  const flowType = getFlowType(formType);

  try {
    gtagEnvelope('event', 'cta_submit_click', {
      cta_name: `Start My Free ${trialLength}-Day Trial`,
      cta_url: `${window.location.origin}${window.location.pathname}${data.successUrl}`,
      cta_position: 'Trial Form',
    });

    await postSegmentService({
      event: 'families_trial_form_clicked',
      properties: {
        reason: getEventReason(),
        source: `Sitecore`,
      },
    });

    const userEmail = existingUserEmail || DOMPurify.sanitize(values.email.toString().toLowerCase());

    // gather form data
    const formData: FormData = {
      email: userEmail,
      firstName: '',
      lastName: '',
      jobTitle: '',
      companyName: '',
      companySize: '',
      phoneNumber: '',
      trialSku: data.sku,
      marketingOptOutShown: marketingOptOutSeen,
      contactPermission,
      clientIp: DOMPurify.sanitize(window.akCIp),
      language: DOMPurify.sanitize(getCookie('lang')) || 'en_US',
      trialLength: trialLength || 30,
      dataResidency: getDataResidencyParam(),
      flowType,
      formType,
      affiliateClickId: DOMPurify.sanitize(getCookie('clickid')),
      segExpQuery,
      segExpValue,
    };

    const bossInvalid = await checkUserValidity({
      email: userEmail,
      trialLength,
      trialSku: data.sku,
      flowType,
    });

    if (!bossInvalid.success) {
      return {
        success: false,
        code: bossInvalid.code,
        correctedEmail: bossInvalid.correctedEmail,
      };
    }
    gtagEnvelope('event', 'trial_success_families', {});
    facebookCAPIGTMEvent({
      contentId: 'lastpass_families',
      email: formData.email,
    });
    await analyticsEvent(formData.email);
    await postTrialInterestBoomiService({
      email: userEmail,
      contactPermission,
      trialSku: data.sku,
      programName: data.inputs?.programName?.defaultValue,
      programID: data.inputs?.programID?.defaultValue,
    });
    await postTrialFormDataServiceLegacy(formData, data.successUrl);
  } catch (error) {
    return {
      success: false,
      code: '500',
      errorMessage: `Families submit error: ${error.message}`,
    };
  }
};
